import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import QRCode from 'qrcode';
import { trackFeatureUsage, trackEvent } from '../utils/analytics';
import { formatDateTimeForBrowser } from '../utils/dateFormat';

import AttendanceCalendarModal from '../components/AttendanceCalendarModal';
import AttendanceDatePicker from '../components/AttendanceDatePicker';
import { attendanceToday, shiftAttendanceDate } from '../utils/attendanceCalendar';

const AttendanceMarker = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [selectedDate, setSelectedDate] = useState(attendanceToday);
  const [snapshotCustomers, setSnapshotCustomers] = useState([]);
  const [snapshotError, setSnapshotError] = useState(false);
  const [calendarCustomer, setCalendarCustomer] = useState(null);
  const snapshotRequest = useRef(0);
  const selectedDateRef = useRef(selectedDate);
  selectedDateRef.current = selectedDate;
  const today = attendanceToday();
  const [qrData, setQrData] = useState(null);
  const [qrImage, setQrImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snapshotAttendance, setSnapshotAttendance] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [manualLoading, setManualLoading] = useState(false);
  const [secondMarkError, setSecondMarkError] = useState('');
  const secondMarkPending = useRef(false);
  const [secondMarkCustomer, setSecondMarkCustomer] = useState(null);
  const [manualError, setManualError] = useState('');
  const manualErrorRef = useRef(null);

  useEffect(() => {
    if (manualError && showManualModal) {
      manualErrorRef.current?.focus({ preventScroll: true });
      manualErrorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    }
  }, [manualError, showManualModal]);
  const [attendanceSearch, setAttendanceSearch] = useState('');
  const [filteredAttendance, setFilteredAttendance] = useState([]);
  const [pendingSearch, setPendingSearch] = useState('');
  const [quickMarkLoadingCustomerId, setQuickMarkLoadingCustomerId] = useState(null);
  const [actionType, setActionType] = useState('mark'); // 'mark' or 'demark'

  useEffect(() => {
    fetchTodayQr();
    fetchActiveClubCustomers();
    trackFeatureUsage('Attendance Marker', 'view');
  }, []);

  useEffect(() => {
    if (showManualModal) {
      fetchActiveClubCustomers();
    }
  }, [showManualModal]);

  useEffect(() => {
    // Filter customers based on search
    if (!customerSearch) {
      setFilteredCustomers(customers);
    } else {
      const filtered = customers.filter(customer =>
        customer.fullName?.toLowerCase().includes(customerSearch.toLowerCase()) ||
        customer.email?.toLowerCase().includes(customerSearch.toLowerCase()) ||
        customer.phone?.toLowerCase().includes(customerSearch.toLowerCase())
      );
      setFilteredCustomers(filtered);
    }
  }, [customers, customerSearch]);

  useEffect(() => {
    // Filter the selected snapshot based on search
    if (!attendanceSearch) {
      setFilteredAttendance(snapshotAttendance);
    } else {
      const filtered = snapshotAttendance.filter(attendance =>
        attendance.customerName?.toLowerCase().includes(attendanceSearch.toLowerCase()) ||
        attendance.customerEmail?.toLowerCase().includes(attendanceSearch.toLowerCase())
      );
      setFilteredAttendance(filtered);
    }
  }, [snapshotAttendance, attendanceSearch]);

  const fetchTodayQr = async () => {
    try {
      const response = await axios.get('/api/attendance/qr/company');
      const data = response.data;
      setQrData(data);
      
      // Generate QR code image
      const qrImageData = await QRCode.toDataURL(data.qrUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#2C5282',
          light: '#FFFFFF'
        }
      });
      setQrImage(qrImageData);
      
      trackEvent('attendance_qr_generated', {
        event_category: 'Attendance',
        event_label: 'QR Generated',
        company_name: data.companyName,
        attendance_date: data.attendanceDate
      });
    } catch (error) {
      console.error('Failed to fetch company QR:', error);
      setError(error.response?.data?.message || t('Failed to generate QR code. Please ensure your company information is set up.'));
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceSnapshot = useCallback(async () => {
    if (selectedDateRef.current !== selectedDate) return;
    const request = ++snapshotRequest.current;
    setAttendanceLoading(true);
    setSnapshotError(false);
    try {
      const response = await axios.get('/api/attendance/snapshot', { params: { date: selectedDate } });
      if (request !== snapshotRequest.current) return;
      setSnapshotAttendance(response.data.attendance || []);
      setSnapshotCustomers(response.data.customers || []);
    } catch (error) {
      if (request === snapshotRequest.current) setSnapshotError(true);
    } finally {
      if (request === snapshotRequest.current) setAttendanceLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchAttendanceSnapshot();
    return () => { snapshotRequest.current += 1; };
  }, [fetchAttendanceSnapshot]);

  const fetchActiveClubCustomers = async () => {
    try {
      const response = await axios.get('/api/attendance/active-club-customers');
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch active club customers:', error);
      setManualError(t('Failed to load customers. Please try again.'));
    }
  };

  const refreshAttendanceView = async () => {
    await Promise.all([
      fetchAttendanceSnapshot(),
      fetchActiveClubCustomers()
    ]);
  };

  const getErrorMessage = (error, fallbackMessage) => {
    const responseData = error?.response?.data;
    if (typeof responseData === 'string' && responseData.trim()) {
      return responseData;
    }
    if (responseData?.message) {
      return responseData.message;
    }
    return fallbackMessage;
  };

  const escapePdfText = (text) => String(text ?? '')
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)');

  const buildTextBlock = (lines, x, y, font = 'F1', size = 12, leading = 16) => {
    const commands = [`BT /${font} ${size} Tf ${x} ${y} Td ${leading} TL`];
    lines.forEach((line, index) => {
      if (index > 0) {
        commands.push('T*');
      }
      commands.push(`(${escapePdfText(line)}) Tj`);
    });
    commands.push('ET');
    return commands.join('\n');
  };

  const buildInstructionPdf = (jpegBytes, contentStream) => {
    const encoder = new TextEncoder();
    const chunks = [];
    const offsets = [0];
    let byteLength = 0;

    const pushText = (text) => {
      const bytes = encoder.encode(text);
      chunks.push(bytes);
      byteLength += bytes.length;
    };

    const pushBytes = (bytes) => {
      chunks.push(bytes);
      byteLength += bytes.length;
    };

    const startObject = (objectNumber) => {
      offsets[objectNumber] = byteLength;
      pushText(`${objectNumber} 0 obj\n`);
    };

    pushText('%PDF-1.4\n');

    startObject(1);
    pushText('<< /Type /Catalog /Pages 2 0 R >>\nendobj\n');

    startObject(2);
    pushText('<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n');

    startObject(3);
    pushText('<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 4 0 R /F2 5 0 R >> /XObject << /Im1 6 0 R >> >> /Contents 7 0 R >>\nendobj\n');

    startObject(4);
    pushText('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\nendobj\n');

    startObject(5);
    pushText('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>\nendobj\n');

    startObject(6);
    pushText(`<< /Type /XObject /Subtype /Image /Width 300 /Height 300 /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpegBytes.length} >>\nstream\n`);
    pushBytes(jpegBytes);
    pushText('\nendstream\nendobj\n');

    const contentBytes = encoder.encode(contentStream);
    startObject(7);
    pushText(`<< /Length ${contentBytes.length} >>\nstream\n`);
    pushBytes(contentBytes);
    pushText('\nendstream\nendobj\n');

    const xrefOffset = byteLength;
    pushText('xref\n0 8\n');
    pushText('0000000000 65535 f \n');
    for (let i = 1; i <= 7; i += 1) {
      pushText(`${String(offsets[i]).padStart(10, '0')} 00000 n \n`);
    }
    pushText('trailer\n<< /Size 8 /Root 1 0 R >>\n');
    pushText(`startxref\n${xrefOffset}\n%%EOF`);

    return new Blob(chunks, { type: 'application/pdf' });
  };

  const convertQrToJpegBytes = async () => {
    if (!qrImage) {
      throw new Error('QR code image is not ready yet.');
    }

    const img = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Failed to load QR image.'));
      image.src = qrImage;
    });

    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);

    const jpegDataUrl = canvas.toDataURL('image/jpeg', 0.95);
    const base64 = jpegDataUrl.split(',')[1];
    const binary = atob(base64);
    return Uint8Array.from(binary, (char) => char.charCodeAt(0));
  };

  const downloadAttendanceInstructions = async () => {
    try {
      const jpegBytes = await convertQrToJpegBytes();
      const contentParts = [
        '0.95 0.98 1 rg 40 710 515 90 re f',
        '0 0 0 rg',
        buildTextBlock(['CalorieNSweat Attendance QR Instructions'], 55, 770, 'F2', 20, 20),
        buildTextBlock([
          `Club: ${qrData?.companyName || 'CalorieNSweat Club'}`,
          'Scan this QR code from the CalorieNSweat app to mark attendance.'
        ], 55, 738, 'F1', 11, 15),
        '0.92 0.95 1 rg 40 85 515 600 re f',
        '0 0 0 rg',
        buildTextBlock([
          'Instructions For Club Customers',
          '1. Open the CalorieNSweat app and sign in.',
          '2. Tap the attendance scanner or open the QR attendance link.',
          '3. Scan this QR code and verify the club name and date shown.',
          '4. Tap Mark Attendance to confirm presence for today.',
          '5. If your attendance is marked show it to the attendant & enjoy your',
          '   shake servings.',
          '6. If you face any issues contact your coach.'
        ], 55, 644, 'F1', 12, 18),
        '0 0 0 rg',
        buildTextBlock([
          'Scan To Mark Attendance'
        ], 215, 464, 'F2', 14, 16),
        'q 160 0 0 160 218 279 cm /Im1 Do Q',
        '0 0 0 rg',
        buildTextBlock([
          `Generated by: ${user?.fullName || user?.email || 'System Editor'}`,
          `Generated on: ${new Date().toLocaleDateString('en-GB')}`
        ], 55, 125, 'F1', 10, 14)
      ];

      const pdfBlob = buildInstructionPdf(jpegBytes, contentParts.join('\n'));
      const downloadUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `attendance-instructions-${(qrData?.companyName || 'caloriensweat').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);

      trackEvent('attendance_instruction_pdf_downloaded', {
        event_category: 'Attendance',
        event_label: 'Instruction PDF Downloaded',
        company_name: qrData?.companyName
      });
    } catch (downloadError) {
      console.error('Failed to generate attendance instruction PDF:', downloadError);
      setError(downloadError.message || t('Failed to download instructions PDF. Please try again.'));
    }
  };

  const handleQuickMarkToday = async (customer) => {
    setQuickMarkLoadingCustomerId(customer.id);
    setError('');

    try {
      const today = attendanceToday();
      await axios.post('/api/attendance/manual-mark', {
        customerId: customer.id,
        dates: [today]
      });

      trackEvent('manual_attendance_marked_from_pending_list', {
        event_category: 'Attendance',
        event_label: 'Marked From Yet To Mark List',
        customer_id: customer.id
      });

      await refreshAttendanceView();
    } catch (error) {
      console.error('Failed to mark today attendance:', error);
      setError(getErrorMessage(error, t('Failed to mark attendance for today. Please try again.')));
    } finally {
      setQuickMarkLoadingCustomerId(null);
    }
  };

  const handleSecondAttendance = async customerId => {
    if (secondMarkPending.current) return;
    secondMarkPending.current = true;
    setSecondMarkCustomer(customerId); setSecondMarkError('');
    try {
      await axios.post('/api/attendance/second', { customerId, date: selectedDate });
      await fetchAttendanceSnapshot();
    } catch (failure) { setSecondMarkError({ customerId, message: getErrorMessage(failure, t('Could not mark second attendance.')) }); }
    finally { secondMarkPending.current = false; setSecondMarkCustomer(null); }
  };

  const handleManualAttendanceAction = async () => {
    if (!selectedCustomer || selectedDates.length === 0) {
      setManualError(t('Please select a customer and at least one date'));
      return;
    }

    setManualLoading(true);
    setManualError('');

    try {
      const endpoint = actionType === 'mark' ? '/api/attendance/manual-mark' : '/api/attendance/manual-demark';
      await axios.post(endpoint, {
        customerId: selectedCustomer.id,
        dates: selectedDates
      });

      trackEvent(`manual_attendance_${actionType}ed`, {
        event_category: 'Attendance',
        event_label: `Manual Attendance ${actionType === 'mark' ? 'Marked' : 'Demarked'}`,
        customer_id: selectedCustomer.id,
        dates_count: selectedDates.length
      });

      setShowManualModal(false);
      setSelectedCustomer(null);
      setSelectedDates([]);
      setCustomerSearch('');
      setActionType('mark');
      fetchAttendanceSnapshot(); // Refresh the selected snapshot
      
      alert(actionType === 'mark'
        ? t('Attendance marked successfully for selected dates!')
        : t('Attendance de-marked successfully for selected dates!'));
    } catch (error) {
      console.error(`Failed to ${actionType} manual attendance:`, error);
      setManualError(getErrorMessage(
        error,
        actionType === 'mark'
          ? t('Failed to mark attendance. Please try again.')
          : t('Failed to de-mark attendance. Please try again.')
      ));
    } finally {
      setManualLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading">{t('Generating attendance QR code...')}</div>
      </div>
    );
  }

  const uniqueAttendanceMap = new Map();
  snapshotAttendance.forEach((attendance) => {
    if (attendance.customerId && !uniqueAttendanceMap.has(attendance.customerId)) {
      uniqueAttendanceMap.set(attendance.customerId, attendance);
    }
  });
  const uniqueSnapshotAttendance = Array.from(uniqueAttendanceMap.values());

  const uniqueCustomerMap = new Map();
  snapshotCustomers.forEach((customer) => {
    if (customer.id && !uniqueCustomerMap.has(customer.id)) {
      uniqueCustomerMap.set(customer.id, customer);
    }
  });
  const uniqueCustomers = Array.from(uniqueCustomerMap.values());

  const presentCustomerIds = new Set(uniqueSnapshotAttendance.map(attendance => attendance.customerId).filter(Boolean));
  const uniqueFilteredAttendanceMap = new Map();
  filteredAttendance.forEach((attendance) => {
    if (attendance.customerId && !uniqueFilteredAttendanceMap.has(attendance.customerId)) {
      uniqueFilteredAttendanceMap.set(attendance.customerId, attendance);
    }
  });
  const displayedPresentAttendance = Array.from(uniqueFilteredAttendanceMap.values());
  const pendingCustomers = uniqueCustomers.filter(customer => !presentCustomerIds.has(customer.id));
  const filteredPendingCustomers = pendingCustomers.filter(customer =>
    !pendingSearch ||
    customer.fullName?.toLowerCase().includes(pendingSearch.toLowerCase()) ||
    customer.email?.toLowerCase().includes(pendingSearch.toLowerCase()) ||
    customer.phone?.toLowerCase().includes(pendingSearch.toLowerCase())
  );

  return (
    <div className="page" style={{ maxWidth: 'none', paddingLeft: '16px', paddingRight: '16px' }}>
      <h1 className="page-title">{t('Attendance Marker')}</h1>
      
      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      {qrData && (
        <div style={{
          width: '100%',
          maxWidth: '1680px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: '340px minmax(0, 1fr)',
          gap: '24px',
          alignItems: 'start'
        }}>
          <div className="card" style={{ position: 'sticky', top: '20px' }}>
            <div style={{ textAlign: 'center' }}>
              <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
                📱 {t('Attendance QR Code')}
              </h3>
              
              {qrImage && (
                <div style={{
                  display: 'inline-block',
                  padding: '16px',
                  background: 'var(--white)',
                  borderRadius: '12px',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                  border: '3px solid var(--primary-blue)',
                  maxWidth: '100%'
                }}>
                  <img 
                    src={qrImage} 
                    alt="Attendance QR Code" 
                    style={{ 
                      width: '100%',
                      maxWidth: '280px',
                      height: 'auto',
                      display: 'block'
                    }} 
                  />
                </div>
              )}
              
              <div style={{
                marginTop: '20px',
                padding: '15px',
                background: 'var(--pale-blue)',
                borderRadius: '8px',
                fontSize: '14px',
                color: 'var(--dark-blue)',
                textAlign: 'left'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    {t('QR Code Details')}
                  </div>
                <div>{t("Today's Date")}: {qrData.attendanceDateDisplay}</div>
                <div>{t('Club')}: {qrData.companyName}</div>
                <div style={{ fontSize: '12px', marginTop: '8px', color: 'var(--gray-dark)', wordBreak: 'break-all' }}>
                  UUID: {qrData.uuid}
                </div>
                <div style={{ fontSize: '12px', marginTop: '8px', color: 'var(--success)', fontWeight: 'bold' }}>
                  {t('Permanent QR. No daily printing required.')}
                </div>
              </div>

              <button
                className="btn btn-primary"
                onClick={downloadAttendanceInstructions}
                style={{
                  marginTop: '16px',
                  width: '100%',
                  background: 'linear-gradient(135deg, #2C5282 0%, #2B6CB0 100%)',
                  border: 'none'
                }}
              >
                {t('Download Instructions PDF')}
              </button>
            </div>
          </div>

          <div className="card">
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
                  📋 {t("Attendance snapshot")}
                </h3>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setActionType('mark');
                      setShowManualModal(true);
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #48BB78 0%, #38A169 100%)',
                      border: 'none'
                    }}
                  >
                    ✏️ {t('Mark Attendance')}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setActionType('demark');
                      setShowManualModal(true);
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #E53E3E 0%, #C53030 100%)',
                      border: 'none'
                    }}
                  >
                    ❌ {t('De-Mark Attendance')}
                  </button>
                </div>
              </div>
              
              <div style={{
                background: 'var(--pale-blue)',
                padding: '20px',
                borderRadius: '12px',
                border: '2px solid var(--primary-blue)',
                marginBottom: '20px'
              }}>
                <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px', textAlign: 'center' }}>
                  {qrData.companyName}
                </h4>
                <div style={{ 
                  fontSize: '16px', 
                  color: 'var(--gray-dark)',
                  textAlign: 'center',
                  marginBottom: '10px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
                    <button className="btn btn-secondary" aria-label={t('Previous day')} disabled={selectedDate === '0001-01-01'} onClick={() => setSelectedDate(shiftAttendanceDate(selectedDate, -1))}>←</button>
                    <label>{t('Date')} <input type="date" value={selectedDate} min="0001-01-01" max={today} onChange={event => { if (event.target.value && event.target.validity.valid) setSelectedDate(event.target.value); }} /></label>
                    <button className="btn btn-secondary" aria-label={t('Next day')} disabled={selectedDate >= today} onClick={() => setSelectedDate(shiftAttendanceDate(selectedDate, 1))}>→</button>
                    <button className="btn btn-secondary" onClick={() => setSelectedDate(today)}>{t('Today')}</button>
                  </div>
                  <p style={{ fontSize: '12px', marginTop: '10px' }}>{t('Remaining customers use the current active club roster, excluding customers created after this date. Dates use India time.')}</p>
                </div>
                <div style={{ 
                  fontSize: '14px', 
                  color: 'var(--success)',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  ✅ {t('Permanent QR Code - Valid Every Day')}
                </div>
              </div>

              {snapshotError ? (
                <div role="alert">{t('Could not load attendance.')} <button className="btn btn-secondary" onClick={fetchAttendanceSnapshot}>{t('Retry')}</button></div>
              ) : attendanceLoading ? (
                <div className="loading">{t("Loading attendance...")}</div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '20px',
                  alignItems: 'start'
                }}>
                  <div style={{
                    background: 'var(--white)',
                    borderRadius: '8px',
                    border: '1px solid var(--gray-medium)',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      background: 'var(--pale-blue)',
                      padding: '15px',
                      borderBottom: '1px solid var(--gray-medium)',
                      fontWeight: 'bold',
                      color: 'var(--dark-blue)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      <span>{t('Customers Present ({count})', { count: uniqueSnapshotAttendance.length })}</span>
                      <input
                        type="text"
                        placeholder={t('Search present...')}
                        value={attendanceSearch}
                        onChange={(e) => setAttendanceSearch(e.target.value)}
                        style={{
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid var(--gray-medium)',
                          fontSize: '14px',
                          width: '180px'
                        }}
                      />
                    </div>
                    <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
                      {displayedPresentAttendance.length === 0 ? (
                        <div style={{
                          textAlign: 'center',
                          padding: '20px',
                          color: 'var(--gray-dark)'
                        }}>
                          {attendanceSearch ? t('No matching attendance found.') : t('No attendance recorded for this date.')}
                        </div>
                      ) : (
                        displayedPresentAttendance.map((attendance, index, list) => (
                          <div
                            key={attendance.customerId || attendance.id}
                            style={{
                              padding: '12px 15px',
                              borderBottom: index < list.length - 1 ? '1px solid var(--gray-light)' : 'none',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              background: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)'
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: '500', color: 'var(--dark-blue)' }}>
                                <button className="attendance-customer-link" onClick={() => setCalendarCustomer({ id: attendance.customerId, fullName: attendance.customerName })}>{attendance.customerName}</button>
                              </div>
                              <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                                {attendance.customerEmail}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{
                                background: 'var(--success)',
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                marginBottom: '4px'
                              }}>
                                ✓ {t('Present')} ({attendance.attendanceCount || 1}/2)
                              </div>
                              <div style={{ fontSize: '11px', color: 'var(--gray-dark)' }}>
                                {formatDateTimeForBrowser(attendance.markedAt)}
                              </div>
                              {(attendance.attendanceCount || 1) < 2 && attendance.canAddSecond && <button className="btn btn-secondary"
                                disabled={secondMarkCustomer !== null} onClick={() => handleSecondAttendance(attendance.customerId)}>
                                {secondMarkCustomer === attendance.customerId ? t('Marking...') : t('+1 attendance')}
                              </button>}
                              {secondMarkError?.customerId === attendance.customerId && <div role="alert" className="alert alert-error">{secondMarkError.message}</div>}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div style={{
                    background: 'var(--white)',
                    borderRadius: '8px',
                    border: '1px solid var(--gray-medium)',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      background: '#FFF7ED',
                      padding: '15px',
                      borderBottom: '1px solid var(--gray-medium)',
                      fontWeight: 'bold',
                      color: '#9C4221',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      <span>{t('Remaining Customers ({count})', { count: pendingCustomers.length })}</span>
                      <input
                        type="text"
                        placeholder={t('Search pending...')}
                        value={pendingSearch}
                        onChange={(e) => setPendingSearch(e.target.value)}
                        style={{
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid var(--gray-medium)',
                          fontSize: '14px',
                          width: '180px'
                        }}
                      />
                    </div>
                    <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
                      {filteredPendingCustomers.length === 0 ? (
                        <div style={{
                          textAlign: 'center',
                          padding: '20px',
                          color: 'var(--gray-dark)'
                        }}>
                          {pendingSearch ? t('No matching customers found.') : t('No remaining customers for this date.')}
                        </div>
                      ) : (
                        filteredPendingCustomers.map((customer, index) => (
                          <div
                            key={customer.id}
                            style={{
                              padding: '12px 15px',
                              borderBottom: index < filteredPendingCustomers.length - 1 ? '1px solid var(--gray-light)' : 'none',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: '12px',
                              background: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)'
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: '500', color: 'var(--dark-blue)' }}>
                                <button className="attendance-customer-link" onClick={() => setCalendarCustomer(customer)}>{customer.fullName}</button>
                              </div>
                              <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                                {customer.email}
                              </div>
                              {customer.phone && (
                                <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                                  {customer.phone}
                                </div>
                              )}
                            </div>
                            <button
                              className="btn btn-primary"
                              onClick={() => handleQuickMarkToday(customer)}
                              disabled={selectedDate !== today || quickMarkLoadingCustomerId === customer.id}
                              title={selectedDate !== today ? t('Use Mark Attendance to edit past dates') : undefined}
                              style={{
                                whiteSpace: 'nowrap',
                                background: 'linear-gradient(135deg, #48BB78 0%, #38A169 100%)',
                                border: 'none',
                                minWidth: '125px'
                              }}
                            >
                              {quickMarkLoadingCustomerId === customer.id ? t('Marking...') : t('Mark Today')}
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    fetchTodayQr();
                    refreshAttendanceView();
                  }}
                  disabled={loading}
                >
                  🔄 {t('Refresh')}
                </button>
              </div>
            </div>
          </div>
        </div>
        </div>
      )}

      {calendarCustomer && <AttendanceCalendarModal customer={calendarCustomer} initialDate={selectedDate} onChanged={fetchAttendanceSnapshot} onClose={() => setCalendarCustomer(null)} />}

      {/* Manual Attendance Modal */}
      {showManualModal && (
        <div role="dialog" aria-modal="true" aria-label={t('Manual attendance')} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '760px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
                {actionType === 'mark' ? `✏️ ${t('Manually Mark Attendance')}` : `❌ ${t('Manually De-Mark Attendance')}`}
              </h3>
              <button
                onClick={() => {
                  setShowManualModal(false);
                  setSelectedCustomer(null);
                  setSelectedDates([]);
                  setCustomerSearch('');
                  setManualError('');
                  setActionType('mark');
                }}
                disabled={manualLoading}
                aria-label={t('Close manual attendance')}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--gray-dark)'
                }}
              >
                ×
              </button>
            </div>

            {/* Customer Search */}
            <div className="form-group">
              <label htmlFor="customerSearch" className="form-label">
                {t('Search Customer')} *
              </label>
              <input
                type="text"
                id="customerSearch"
                className="form-input"
                placeholder={t('Search by name, email, or phone...')}
                value={customerSearch}
                onChange={(e) => {
                  setCustomerSearch(e.target.value);
                  setSelectedCustomer(null);
                  setSelectedDates([]);
                  setManualError('');
                }}
                disabled={manualLoading}
              />
            </div>

            {/* Customer Selection */}
            {filteredCustomers.length > 0 && customerSearch && (
              <div style={{
                border: '1px solid var(--gray-medium)',
                borderRadius: '6px',
                maxHeight: '200px',
                overflowY: 'auto',
                marginBottom: '20px'
              }}>
                {filteredCustomers.map((customer) => (
                  <div
                    key={customer.id}
                    role="button"
                    tabIndex={manualLoading ? -1 : 0}
                    aria-disabled={manualLoading}
                    onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.currentTarget.click(); } }}
                    onClick={() => {
                      if (manualLoading) return;
                      if (selectedCustomer?.id !== customer.id) setSelectedDates([]);
                      setManualError('');
                      setSelectedCustomer(customer);
                      setCustomerSearch(customer.fullName);
                    }}
                    style={{
                      padding: '12px',
                      borderBottom: '1px solid var(--gray-light)',
                      cursor: 'pointer',
                      background: selectedCustomer?.id === customer.id ? 'var(--pale-blue)' : 'transparent'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--pale-blue)'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = selectedCustomer?.id === customer.id ? 'var(--pale-blue)' : 'transparent'}
                  >
                    <div style={{ fontWeight: '500', color: 'var(--dark-blue)' }}>
                      {customer.fullName}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                      {customer.email} • {customer.phone}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Selected Customer Display */}
            {selectedCustomer && (
              <div style={{
                background: 'var(--pale-blue)',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '20px',
                border: '2px solid var(--primary-blue)'
              }}>
                <h5 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>
                  {t('Selected Customer')}:
                </h5>
                <div style={{ fontWeight: '500' }}>{selectedCustomer.fullName}</div>
                <div style={{ fontSize: '14px', color: 'var(--gray-dark)' }}>
                  {selectedCustomer.email} • {selectedCustomer.phone}
                </div>
              </div>
            )}

            {selectedCustomer && actionType === 'demark' && <p>{t('Removing attendance removes all visits for each selected day. Additional guest consumption is undone separately in the attendance calendar.')}</p>}
            {selectedCustomer && (
              <AttendanceDatePicker key={`${selectedCustomer.id}-${actionType}`}
                customerId={selectedCustomer.id} initialDate={selectedDate} actionType={actionType}
                selectedDates={selectedDates} onChange={setSelectedDates} disabled={manualLoading} />
            )}

            {manualError && (
              <div ref={manualErrorRef} tabIndex={-1} role="alert" className="alert alert-error"
                style={{ margin: '16px 0' }}>
                <strong>{t('Attendance was not saved.')}</strong> {manualError}
                <div>{t('Your selected dates are kept. Adjust the selection and try again.')}</div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowManualModal(false);
                  setSelectedCustomer(null);
                  setSelectedDates([]);
                  setCustomerSearch('');
                  setManualError('');
                  setActionType('mark');
                }}
                disabled={manualLoading}
              >
                {t('Cancel')}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleManualAttendanceAction}
                disabled={manualLoading || !selectedCustomer || selectedDates.length === 0}
                style={{
                  background: actionType === 'mark' 
                    ? 'linear-gradient(135deg, #48BB78 0%, #38A169 100%)'
                    : 'linear-gradient(135deg, #E53E3E 0%, #C53030 100%)',
                  border: 'none'
                }}
              >
                {manualLoading 
                  ? (actionType === 'mark' ? t('Marking...') : t('De-marking...'))
                  : (actionType === 'mark'
                    ? t('Mark attendance for {count} selected days', { count: selectedDates.length })
                    : t('Remove attendance for {count} selected days', { count: selectedDates.length }))
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceMarker;
